import { movies, type Movie, type MediaType } from "@/mocks/movies";
import { newsList, type NewsItem } from "@/mocks/news";
import { topics, type Topic } from "@/mocks/topics";

export const mediaTypeLabel: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

export const mediaTypes: MediaType[] = ["movie", "tv", "variety"];

export function isMediaType(value: string | undefined): value is MediaType {
  return value === "movie" || value === "tv" || value === "variety";
}

export function getMovieById(id: string | undefined): Movie | undefined {
  return movies.find((item) => item.id === id);
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  return newsList.find((item) => item.id === id);
}

export function getTopicById(id: string | undefined): Topic | undefined {
  return topics.find((item) => item.id === id);
}

export function getMoviesByType(type: MediaType): Movie[] {
  return movies.filter((item) => item.type === type);
}

export function getGenresByType(type: MediaType): string[] {
  const set = new Set<string>();
  getMoviesByType(type).forEach((item) => item.genres.forEach((g) => set.add(g)));
  return Array.from(set);
}

export function getRegionsByType(type: MediaType): string[] {
  const set = new Set<string>();
  getMoviesByType(type).forEach((item) => set.add(item.region));
  return Array.from(set);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const sameType = movies.filter((item) => item.id !== movie.id && item.type === movie.type);
  const scored = sameType
    .map((item) => ({
      item,
      weight:
        item.genres.filter((g) => movie.genres.includes(g)).length * 10 +
        (item.region === movie.region ? 3 : 0) +
        item.hot / 100,
    }))
    .sort((a, b) => b.weight - a.weight);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getRelatedNews(item: NewsItem, limit = 3): NewsItem[] {
  const others = newsList.filter((n) => n.id !== item.id);
  const sameCategory = others.filter((n) => n.category === item.category);
  const rest = others.filter((n) => n.category !== item.category);
  return [...sameCategory, ...rest].slice(0, limit);
}

export function getLatestNews(limit = 5): NewsItem[] {
  return [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getHotMovies(limit = 5): Movie[] {
  return [...movies].sort((a, b) => b.hot - a.hot).slice(0, limit);
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((item) =>
    [item.title, item.director, item.region, ...item.genres, ...item.cast]
      .join(" ")
      .toLowerCase()
      .includes(q)
  );
}

export function searchNews(keyword: string): NewsItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return newsList.filter((item) =>
    [item.title, item.excerpt, item.author, item.category].join(" ").toLowerCase().includes(q)
  );
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  if (!year || !month || !day) return date;
  return `${year}.${month}.${day}`;
}

export function getTopicMovies(topic: Topic): Movie[] {
  return topic.movieIds
    .map((id) => movies.find((item) => item.id === id))
    .filter((item): item is Movie => Boolean(item));
}

export function getTopicNews(topic: Topic): NewsItem[] {
  return topic.newsIds
    .map((id) => newsList.find((item) => item.id === id))
    .filter((item): item is NewsItem => Boolean(item));
}

export { movies, newsList, topics };
export type { Movie, NewsItem, Topic, MediaType };